import {
  DataPagination,
  DataTagFilters,
} from '@robovision/data'
import {
  addOrReplaceByKey,
  distinctByKey,
} from '@robovision/rvai-utils'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import slugify from 'slugify'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import { Tag } from '~domain/Tag'
import {
  dataApi,
  hubApi,
} from '~services/api'
import { getTagMetadata } from '~utils/api/getTagMetadata'
import { listFilteredPoolTags } from '~utils/api/listFilteredPoolTags'
import { useSessionStore } from './useSessionStore'


interface DeleteTags {
  cascadeSamples: boolean
  projectId: string
  tagId: string
}

export const useTagsStore = defineStore('tags', () => {
  const state = reactive({
    projectTags: new Map<string, Tag[]>(),
    thumbnailUrls: new Map<string, Promise<string | undefined>>(),
  })

  const getters = {
    getTags (
      projectId: string,
      {
        algorithmJobId,
        artifactId,
      }: {
        algorithmJobId?: string
        artifactId?: string
      } = {},
    ): Tag[] {
      return (state.projectTags.get(projectId) ?? []).filter(j => {
        return !!(
          (
            !algorithmJobId || j.attributes.algorithmJobId === algorithmJobId
          ) && (
            // Backwards compatibility: if now artifact ID attribute is set, show it
            !artifactId || !j.attributes.artifactId || j.attributes.artifactId === artifactId
          )
        )
      })
    },
  }

  const actions = {
    async createTag (projectId: string, name: string): Promise<void> {
      const { currentUser } = useSessionStore()
      const { data: datapools } = await hubApi.datapools.listDatapools({ projectId })
      if (!datapools.items[0]) return

      const [{ id: poolId }] = datapools.items
      const { data: { tagId } } = await dataApi.pools.createTag(poolId, {
        attributes: {},
        name,
        // the ownerId is required for the API to correctly return the data for the data center table
        ownerId: currentUser?.id,
      })

      const tag = await getTagMetadata(tagId)

      if (tag == null) throw new Error('Unreachable: tag metadata should be there')

      state.projectTags.set(projectId, [
        tag,
        ...(state.projectTags.get(projectId) ?? []),
      ])
    },

    async deleteTags ({
      cascadeSamples,
      projectId,
      tagId,
    }: DeleteTags) {
      await dataApi.tags.deleteTag(tagId, {
        deleteSamples: cascadeSamples,
      })

      const remainingTags = state.projectTags.get(projectId)?.filter(t => t.tagId !== tagId) ?? []
      state.projectTags.set(projectId, remainingTags)
    },

    async getThumbnailUrl (projectId: string, tagId: string): Promise<string | undefined> {
      if (!state.thumbnailUrls.has(tagId)) {
        const promise = (async () => {
          try {
            const tag = state.projectTags.get(projectId)?.find(tag => tag.tagId === tagId)
            if (!tag) return undefined
            const { data: { samples } } = await dataApi.pools.listPoolSamples(
              tag.poolId,
              {
                limit: 1,
                tagIds: [[tagId]],
              },
            )
            const [sample] = samples
            return sample && `/datastore${sample.thumbnail}`
          } catch (e) {
            console.error(e)
          }
        })()
        state.thumbnailUrls.set(tagId, promise)
      }
      return state.thumbnailUrls.get(tagId)
    },

    async loadProjectTag (projectId: string, tagId: string, signal?: AbortSignal): Promise<Tag | undefined> {
      try {
        const tag = await getTagMetadata(tagId, undefined, { signal })

        if (!tag) return undefined

        const existingTags = state.projectTags.get(projectId) ?? []
        const updatedProjectTags = [...existingTags.filter(t => t.tagId !== tag.tagId), tag]

        state.projectTags.set(projectId, updatedProjectTags)
        return tag
      } catch (e) {
        if (e instanceof AxiosError && e.code === 'ERR_CANCELED') {
          // do nothing
        } else {
          throw e
        }
      }
    },

    async loadProjectTags (
      projectId: string,
      {
        includeSampleCount,
        pagination = { limit: null },
        tagFilters,
      }: {
        includeSampleCount?: boolean
        pagination?: DataPagination
        tagFilters?: DataTagFilters
      } = {},
    ): Promise<void> {
      const { data: datapools } = await hubApi.datapools.listDatapools({ projectId })
      if (!datapools.items[0]) return

      const [{ id: poolId }] = datapools.items
      const { tags } = await listFilteredPoolTags(poolId, {
        includeSampleCount,
        pagination,
        tagFilters,
      })

      state.projectTags.set(projectId, distinctByKey(tags, 'tagId'))
    },

    async removeProjectTag (projectId: string, tagId: string, cascadeSamples: boolean): Promise<void> {
      const { data: datapools } = await hubApi.datapools.listDatapools({ projectId })
      if (!datapools.items[0]) return

      await this.deleteTags({
        cascadeSamples,
        projectId,
        tagId,
      })
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },

    setTags (projectId: string, tags: Tag[]) {
      state.projectTags.set(projectId, tags)
    },

    setTagSampleCount (projectId: string, tagId: string, sampleCount: number) {
      const tag = state.projectTags
        .get(projectId)
        ?.find(t => t.tagId === tagId)
      if (!tag) return
      tag.sampleCount = sampleCount
    },

    async updateTagName (projectId: string, tagId: string, name: string) {
      const prevTag = await getTagMetadata(tagId)
      if (!prevTag) throw new Error('Unreachable: tag metadata should be there')
      const newMetadata = {
        attributes: prevTag.attributes,
        description: prevTag.description,
        name,
        ownerId: prevTag.ownerId, // weird?
        slug: slugify(name),
      }
      await dataApi.tags.updateTagMetadata(tagId, newMetadata)

      state.projectTags.set(projectId, addOrReplaceByKey(
        state.projectTags.get(projectId) ?? [],
        prevTag.withUpdates(newMetadata),
        'tagId',
      ))
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...getters,
    ...actions,
  }
})
